import { createUseStyles, Styles, useTheme } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { useCallback, useRef } from 'react';
import { EventContentArg } from '@fullcalendar/core';
import moment from 'moment';

const useStyles = createUseStyles((theme: Theme) => ({
  baseEvent: {
    '& .fc-event-time, & .fc-event-title': {
      ...theme.fonts.calendarText,
      paddingLeft: theme.marginBase,
      color: theme.colors.inkPrimary,
    },
    ' & .fc-event-title': {
      fontWeight: 700,
      whiteSpace: 'pre-line',
    },

    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    borderLeft: {
      width: 4,
      style: 'solid',
      color: theme.colors.inkSecondary,
    },
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,

    boxShadow: theme.boxShadow.base,
  },
  small: {
    '& .fc-event-time, & .fc-event-title': {
      fontSize: 10,
    },
  },
  eventFree: {
    background: `repeating-linear-gradient(45deg, ${theme.colors.grayBackground} 0px, ${theme.colors.grayBackground} 20px, white 20px, white 40px)`,
    opacity: 0.8,
  },
  event: {},
  availability: {
    backgroundColor: theme.colors.greenMuted,
    border: {
      style: 'dashed',
      color: theme.colors.greenAccessible,
      width: 1,
    },
    '&:hover': {
      borderStyle: 'solid',
      borderColor: theme.colors.green,
    },
    '& .fc-event-time': {
      color: theme.colors.greenAccessible,
    },
  },
  appointment: {
    backgroundColor: theme.colors.greenAccessible,
    borderLeftColor: theme.colors.green,

    '& .fc-event-time, & .fc-event-title': {
      color: theme.colors.white,
    },
  },
}));

const colorList: Colors[] = [
  'blue',
  'green',
  'purple',
  'yellow',
  'red',
  'accent',
];

const useEventColorStyle = createUseStyles((theme: Theme) =>
  colorList.reduce((acc, color) => {
    return {
      ...acc,
      [color]: {
        borderLeftColor: theme.colorDefinitions[color].std,
        '& .fc-event-time, & .fc-event-title': {
          color: theme.colorDefinitions[color].accessible,
        },
      },
    };
  }, {} as Styles<Colors>),
);

export enum EventType {
  AVAILABILITY = 'AVAILABILITY',
  APPOINTMENT = 'APPOINTMENT',
  EVENT = 'EVENT',
}

export const useGetEventClassNames = () => {
  const theme: Theme = useTheme();
  const classes = useStyles();
  const colorClasses = useEventColorStyle();
  const resourceColorRef = useRef<Record<string, Colors>>({});
  const colorIterator = useRef<number>(0);

  return useCallback(
    (args: EventContentArg) => {
      const { event } = args;
      if (event.display === 'background') {
        return '';
      }

      const classNames = [classes.baseEvent];
      const type: EventType = event.extendedProps.type || EventType.EVENT;

      const duration = moment(event.end).diff(event.start, 'minutes');
      if (duration <= 30) {
        classNames.push(classes.small);
      }

      if (type === EventType.AVAILABILITY) {
        classNames.push(classes.availability);
        return classNames;
      }
      if (type === EventType.APPOINTMENT) {
        classNames.push(classes.appointment);
        return classNames;
      }

      classNames.push(classes.event);

      if (event.extendedProps.freeBusyStatus === 'free') {
        classNames.push(classes.eventFree);
      }

      const resourceId = event.extendedProps.resourceId;
      if (!resourceId) {
        return classNames;
      }
      if (!resourceColorRef.current[resourceId]) {
        resourceColorRef.current[resourceId] = colorList[colorIterator.current];
        colorIterator.current = (colorIterator.current + 1) % colorList.length;
      }
      const color = resourceColorRef.current[resourceId];
      classNames.push(colorClasses[color]);
      return classNames;
    },
    [classes, theme, resourceColorRef],
  );
};
